import i18next from 'i18next';

export class BackendError extends Error {
  name = 'BackendError';

  statusCode: number;
  body: {};

  constructor(message: string, statusCode: number, body?: {}) {
    super(message);
    Object.setPrototypeOf(this, BackendError.prototype);
    this.statusCode = statusCode;
    this.body = body;
  }
}

export class G8Error extends BackendError {
  name = 'G8Error';

  statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message, statusCode);
    Object.setPrototypeOf(this, G8Error.prototype);
  }
}

const DefaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
  // 'X-XSRF-TOKEN': localStorage.getItem('XSRF-TOKEN'),
};

export const backendBasePath = '/faceless/pwa/1';

const shortDatePattern = /^(\d{4})-([01]\d)-([0-3]\d)$/;
const iso8601DatePattern = /^\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)$/;

function dateReviver(key: string, value: unknown) {
  if (typeof value !== 'string') return value;
  if (!value) return value;
  if (value.length < 9 || value.length > 40) return value;
  /*
    Date-only strings müssen als integer und nicht als string gecastet werden.
    Casten von new Date() mit date-only string wird als UTC angenommen. Dies kann Probleme
    geben wenn man sich in anderen Zeitzonen befindet.

    Beispiel:
    new Date('1970-01-01') // Wed Dec 31 1969 14:00:00 GMT-1000 (Alaska Standard Time)

    jedoch mit Date(year, monthIndex, day):

    new Date(1970, 0, 1) // Thu Jan 01 1970 00:00:00 GMT-1000 (Alaska Standard Time)
  */
  const shortDateMatch = shortDatePattern.exec(value);
  if (shortDateMatch) {
    const [year, month, day] = shortDateMatch.slice(1).map((n) => parseInt(n, 10));
    return new Date(year, month - 1, day); // Monate sind 0-basiert geindext
  }
  if (iso8601DatePattern.test(value)) return new Date(value);

  return value;
}

export function handleFbcFbp() {
  const fbc = document.cookie
    .split('; ')
    .find((row) => row.startsWith('_fbc='))
    ?.split('=')[1];
  const fbp = document.cookie
    .split('; ')
    .find((row) => row.startsWith('_fbp='))
    ?.split('=')[1];
  if (fbc) {
    sessionStorage.setItem('own_fbc', fbc);
  }
  if (fbp) {
    sessionStorage.setItem('own_fbp', fbp);
  }
}

/**
 * addHeaderIfExists is used to add headers to the request if they exist in sessionStorage or URL params
 * @param key
 * @param headerName
 * @param headerObj
 * @param isAUrlParam optional boolean to check if the key is a URL param. if not found, it will check sessionStorage
 */
const addHeaderIfExists = (
  key: string,
  headerName: string,
  headerObj: Record<string, string>,
  isAUrlParam = false
) => {
  let value;
  if (isAUrlParam) {
    const urlParams = new URLSearchParams(window.location.search);
    value = urlParams.get(key);
    if (!value) {
      value = sessionStorage.getItem(key);
    } else {
      sessionStorage.setItem(key, value);
    }
  } else {
    value = sessionStorage.getItem(key);
  }
  if (value) {
    headerObj[headerName] = value;
  }
};

export async function fetchJson<T = unknown>(
  path: string,
  { method = 'get', headers = {}, ...options }: RequestInit = {}
): Promise<T> {
  // console.log('Backend could request', path, 'with Accept-Language:', i18next.language);
  try {
    sessionStorage.setItem('eventId', crypto.randomUUID());
    handleFbcFbp();
    const eventHeaders: Record<string, string> = {};
    addHeaderIfExists('eventId', 'X-Event-Id', eventHeaders);
    addHeaderIfExists('purchaseEventId', 'X-Purchase-Event-Id', eventHeaders);
    addHeaderIfExists('initCheckoutEventId', 'X-InitCheckout-Event-Id', eventHeaders);
    addHeaderIfExists('addToCartEventId', 'X-AddToCart-Event-Id', eventHeaders);
    addHeaderIfExists('fbclid', 'X-fbclid', eventHeaders, true);
    addHeaderIfExists("own_fbc", "X-own_fbc", eventHeaders);
    addHeaderIfExists("own_fbp", "X-own_fbp", eventHeaders);

    if(window.fbq && window.fbq.getState && window.fbq.getState().pixels && window.fbq.getState().pixels[0] && window.fbq.getState().pixels[0].id) {
      addHeaderIfExists(window.fbq.getState().pixels[0].id, 'X-pixelId', eventHeaders);
    }

    const res = await fetch(path, {
      method,
      credentials: 'same-origin', // Needed by Safari 11.1
      headers: {
        ...DefaultHeaders,
        'Accept-Language': i18next.language,
        ...headers,
        ...eventHeaders,
      },
      ...options,
    });
    let text = await res.text();

    /**
     * there is one api route (known to me) that doesn't return the human readable error message in its error.message property:
     * /customer/newsletter_subscriptions/subscribe
     *
     * Instead, it returns an error object { message: "FAILURE", details: "here's the message" }, but only when there's an error because of an already existing email in the database.
     *
     * The next couple of lines look for this message and reassign the variable so the BackendError gets created correctly.
     * The problem arose in NewsletterRegister component, where the Form outputs the BackendError to the user.
     */
    if (typeof text === 'object') {
      const textAsJson = JSON.parse(text);
      if ('message' in textAsJson && 'details' in textAsJson && textAsJson.message === 'FAILURE') {
        text = textAsJson.details;
      }
    }

    if (!res.ok) {
      if (res.status === 503 && res.headers.has('X-G8-Token')) {
        if (window.location.pathname !== '/g8' && !window.location.pathname.startsWith('/orders')) {
          window.location.pathname = '/g8';
          throw new G8Error(text || res.statusText, res.status);
        }
      }
      throw new BackendError(text || res.statusText, res.status);
    }

    if (text) return JSON.parse(text, dateReviver) as T;
    return text as any;
  } catch (error) {
    // Maybe add a logger

    try {
      const parsedBody = JSON.parse(error.message);
      error.message = parsedBody.message;
      error.body = parsedBody;
    } catch (e) {
      /* ignore */
    }

    throw error;
  }
}

export async function del<T = unknown>(path: string, options?: RequestInit) {
  return fetchJson<T>(`${backendBasePath}${path}`, { ...options, method: 'DELETE' });
}

export async function delOldApi<T = unknown>(path: string, options?: RequestInit) {
  return fetchJson<T>(`${path}`, { ...options, method: 'DELETE' });
}

export async function get<T = unknown>(path: string, options?: RequestInit) {
  return fetchJson<T>(`${backendBasePath}${path}`, { ...options, method: 'GET' });
}

export async function head(path: string, options?: RequestInit) {
  return fetchJson<void>(`${backendBasePath}${path}`, { ...options, method: 'HEAD' });
}

export async function options<T = unknown>(path: string, options?: RequestInit) {
  return fetchJson<T>(`${backendBasePath}${path}`, { ...options, method: 'OPTIONS' });
}

export async function patch<T = unknown>(path: string, options?: RequestInit) {
  return fetchJson<T>(`${backendBasePath}${path}`, { ...options, method: 'PATCH' });
}

export async function post<T = unknown>(path: string, options?: RequestInit) {
  return fetchJson<T>(`${backendBasePath}${path}`, { ...options, method: 'POST' });
}

export async function put<T = unknown>(path: string, options?: RequestInit) {
  return fetchJson<T>(`${backendBasePath}${path}`, { ...options, method: 'PUT' });
}

export const Backend = {
  del,
  delOldApi,
  get,
  head,
  options,
  patch,
  post,
  put,
  fetchJson,
};
